import { lazy, Suspense } from 'react'

import { PAGE_ROUTES_PRIVATE, PAGE_ROUTES_PUBLIC } from '@appTypes/enums/pages'
import ErrorBoundary from '@components/ErrorBoundary'
import { AuthGate, RequireAuth, RequireGuest } from '@components/AuthGate'
import { NotFound } from '@components'
import LayoutPrivate from '@layout/Private'
import LayoutPublic from '@layout/Public'
import ChangePassword from '@pages/ChangePassword'

const MutateCourse = lazy(() => import('../pages/MutateCourse'))
const Courses = lazy(() => import('../pages/Courses'))
const Members = lazy(() => import('../pages/Members'))
const CreateMember = lazy(() => import('../pages/CreateMember'))
const Home = lazy(() => import('../pages/Home'))
const ForgotPassword = lazy(() => import('../pages/ForgotPassword'))
const RecoverPassword = lazy(() => import('../pages/RecoverPassword'))
const SignIn = lazy(() => import('../pages/SignIn'))
const OpenTickets = lazy(() => import('../pages/OpenTickets'))
const TicketApprove = lazy(() => import('../pages/TicketApprove'))
const UploadMembers = lazy(() => import('../pages/UploadMembers'))
const TicketsHistory = lazy(() => import('../pages/TicketsHistory'))
const CreateTicket = lazy(() => import('../pages/CreateTicket'))
const Reports = lazy(() => import('../pages/Reports'))
const ReviewRequest = lazy(() => import('../pages/ReviewRequest'))

const privateRouteChildren = [
  {
    path: PAGE_ROUTES_PRIVATE.HOME,
    element: <Home />
  },
  {
    path: PAGE_ROUTES_PRIVATE.OPEN_TICKETS,
    element: <OpenTickets />
  },
  {
    path: PAGE_ROUTES_PRIVATE.OPEN_TICKETS_ID,
    element: <TicketApprove />
  },
  {
    path: PAGE_ROUTES_PRIVATE.TICKET_HISTORY,
    element: <TicketsHistory />
  },
  {
    path: PAGE_ROUTES_PRIVATE.All_COURSES,
    element: <Courses />
  },
  {
    path: PAGE_ROUTES_PRIVATE.CREATE_COURSE,
    element: <MutateCourse />
  },
  {
    path: PAGE_ROUTES_PRIVATE.UPDATE_COURSE,
    element: <MutateCourse />
  },
  {
    path: PAGE_ROUTES_PRIVATE.ALL_MEMBERS,
    element: <Members />
  },
  {
    path: PAGE_ROUTES_PRIVATE.ADD_MEMBER,
    element: <CreateMember />
  },
  {
    path: PAGE_ROUTES_PRIVATE.UPLOAD_MEMBERS,
    element: <UploadMembers />
  },
  {
    path: PAGE_ROUTES_PRIVATE.CREATE_TICKET,
    element: <CreateTicket />
  },
  {
    path: PAGE_ROUTES_PRIVATE.CHANGE_PASSWORD,
    element: <ChangePassword />
  },
  {
    path: PAGE_ROUTES_PRIVATE.REPORTS,
    element: <Reports />
  },
  {
    path: '*',
    element: <NotFound />
  }
]

const publicRouteChildren = [
  {
    path: 'sign-in',
    element: <SignIn />
  },
  {
    path: 'forgot-password',
    element: <ForgotPassword />
  },
  {
    path: 'recover-password',
    element: <RecoverPassword />
  },
  {
    path: '*',
    element: <NotFound />
  }
]

/** Public one-time link — outside AuthGate so guests are not sent to sign-in. */
const reviewRequestRoute = {
  path: PAGE_ROUTES_PUBLIC.REVIEW_REQUEST,
  element: (
    <ErrorBoundary>
      <Suspense fallback={null}>
        <ReviewRequest />
      </Suspense>
    </ErrorBoundary>
  )
}

/** Stable route config — do not recreate on each render. */
export const appRoutes = [
  reviewRequestRoute,
  {
    element: (
      <ErrorBoundary>
        <AuthGate />
      </ErrorBoundary>
    ),
    children: [
      {
        path: '/auth',
        element: <RequireGuest />,
        children: [
          {
            element: <LayoutPublic />,
            children: publicRouteChildren
          }
        ]
      },
      {
        element: <RequireAuth />,
        children: [
          {
            element: <LayoutPrivate />,
            children: privateRouteChildren
          }
        ]
      }
    ]
  }
]
